import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Loader2, Flag, Check, Clock, ListChecks } from 'lucide-react';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' }) : '—');
const STAGE = {
  collected: ['gesammelt — noch nicht freigegeben', 'text-amber-300 border-amber-500/40 bg-amber-500/10'],
  approved: ['freigegeben — noch nicht in der Queue', 'text-sky-300 border-sky-500/40 bg-sky-500/10'],
  in_queue: ['in der iAuto-Queue', 'text-purple-300 border-purple-500/40 bg-purple-500/10'],
  written: ['geschrieben — noch nicht als versendet markiert', 'text-rose-300 border-rose-500/40 bg-rose-500/10'],
  sent: ['versendet', 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'],
};

const Company = ({ l, className }) => l.project_id
  ? <Link to={`/projects/${l.project_id}`} className={`${className} text-foreground hover:text-amber-300`}>{l.company_name}</Link>
  : <span className={`${className} text-foreground`}>{l.company_name}</span>;

// KPI drill-down (Talu, 10.10. / 29.10.2026): which break-ups count in this window, which are written / sent, which are missing.
export function BreakupKpiDrilldown({ weekOffset, kpi }) {
  const kpiKey = kpi?.key || 'team_breakups_sent';
  const isWritten = kpiKey === 'team_breakups_written';
  const verb = isWritten ? 'geschrieben' : 'versendet';
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const show = async () => {
    setOpen(true);
    setLoading(true);
    try { setData((await api.getBreakupKpiLetters(weekOffset, kpiKey)).data); }
    catch (err) { toast.error(err.response?.data?.detail || 'Break-up-Liste konnte nicht geladen werden'); setOpen(false); }
    finally { setLoading(false); }
  };
  const openCount = Math.max(0, (kpi?.target || 0) - (kpi?.total || 0));
  const letters = data?.letters || [];
  const openList = letters.filter(l => !l.done_at);
  const doneList = letters.filter(l => l.done_at);
  return (
    <>
      <button onClick={show} data-testid={`kpi-breakup-drilldown-btn-${kpiKey}`}
        className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-sky-300 hover:text-sky-200 underline underline-offset-2">
        <ListChecks className="w-3 h-3" /> Welche Briefe?{openCount > 0 ? ` (${openCount} offen)` : ''}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`kpi-breakup-drilldown-dialog-${kpiKey}`} aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground flex items-center gap-2">
              <Flag className="w-4 h-4 text-amber-400" /> Break-ups {verb} — dieses Fenster
            </DialogTitle>
          </DialogHeader>
          {loading || !data ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="space-y-3">
              <p className="text-[11px] text-muted-foreground" data-testid="kpi-breakup-drilldown-summary">
                Fällig = Break-ups, die bis Freitag 17:00 gesammelt wurden und bis {fmt(data.deadline)} {verb} sein müssen;
                nicht Erledigte bleiben fällig, gestoppte Sequenzen zählen nicht.
                {' '}<span className="text-emerald-300 font-medium">{data.done} {verb}</span> · <span className={`font-medium ${data.open ? 'text-red-300' : 'text-muted-foreground'}`}>{data.open} offen</span> von {data.due}
                {data.carried_over > 0 && <span className="text-red-300"> · davon {data.carried_over} überfällig aus früheren Fenstern</span>}.
              </p>
              {openList.length > 0 && (
                <div className="border border-red-500/30 rounded-sm divide-y divide-border" data-testid="kpi-breakup-open-list">
                  <p className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-mono text-red-300 flex items-center gap-1"><Clock className="w-3 h-3" /> Noch offen ({openList.length})</p>
                  {openList.map(l => {
                    const [label, cls] = STAGE[l.stage] || STAGE.collected;
                    return (
                      <div key={l.id} className="px-3 py-2 flex items-center gap-2 flex-wrap" data-testid={`kpi-breakup-row-${l.id}`}>
                        <Company l={l} className="text-xs font-medium" />
                        {l.recipient_name && <span className="text-[11px] text-muted-foreground">→ {l.recipient_name}</span>}
                        <span className="text-[10px] text-muted-foreground">gesammelt {fmt(l.collected_at || l.created_at)}</span>
                        <span className={`text-[10px] font-mono ${l.carried_over ? 'text-red-300' : 'text-muted-foreground'}`} data-testid={`kpi-breakup-due-${l.id}`}>
                          fällig {fmt(l.due_at)}{l.carried_over ? ' · überfällig' : ''}
                        </span>
                        <span className={`ml-auto text-[10px] border rounded-sm px-1.5 py-0.5 ${cls}`} data-testid={`kpi-breakup-stage-${l.id}`}>{label}</span>
                        <Link to="/approval-workplace" className="text-[10px] text-sky-300 hover:text-sky-200 underline underline-offset-2">iAuto Workspace</Link>
                      </div>
                    );
                  })}
                </div>
              )}
              {doneList.length > 0 && (
                <div className="border border-border rounded-sm divide-y divide-border" data-testid="kpi-breakup-done-list">
                  <p className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-mono text-emerald-300 flex items-center gap-1"><Check className="w-3 h-3" /> {isWritten ? 'Geschrieben' : 'Versendet'} ({doneList.length})</p>
                  {doneList.map(l => (
                    <div key={l.id} className="px-3 py-1.5 flex items-center gap-2 flex-wrap" data-testid={`kpi-breakup-row-${l.id}`}>
                      <Company l={l} className="text-xs" />
                      {l.recipient_name && <span className="text-[11px] text-muted-foreground">→ {l.recipient_name}</span>}
                      <span className="ml-auto text-[10px] text-emerald-300/80 font-mono">{verb} {fmt(l.done_at)}</span>
                      {isWritten && (l.sent_at
                        ? <span className="text-[10px] text-emerald-300/80 font-mono">· versendet {fmt(l.sent_at)}</span>
                        : <span className="text-[10px] text-rose-300 font-mono">· noch nicht als versendet markiert</span>)}
                    </div>
                  ))}
                </div>
              )}
              {letters.length === 0 && <p className="text-xs text-muted-foreground py-4 text-center">Keine Break-up-Briefe in diesem Fenster fällig.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
